import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShoppingCart, AlertTriangle, Loader2, PackageX, MapPin, Zap, Warehouse, FileSpreadsheet, FileText, Mail, Send, Truck } from "lucide-react";

export default function Riordini() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({ items: [], count: 0, components_count: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/reorder-report"); setData(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const withoutSupplier = data.items.filter((i) => !i.supplier_id);
  const withSupplier = data.items.filter((i) => i.supplier_id);

  const exportData = async (fmt) => {
    setExporting(true);
    try {
      const { data: blob } = await api.get(`/export/reorder?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "pdf" ? "report_riordini.pdf" : "report_riordini.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Esportazione avviata.");
    } catch (e) {
      toast.error("Errore durante l'esportazione.");
    } finally { setExporting(false); }
  };

  const ordineRapido = () => {
    if (withoutSupplier.length === 0) return;
    const lines = withoutSupplier.map((it) =>
      `• ${it.name} [${it.codice_prodotto}] — giacenza ${it.quantity}, soglia ${it.reorder_threshold}, da ordinare ${it.to_order}`
    ).join("\n");
    const subject = `Richiesta di riordino — ${withoutSupplier.length} articoli senza fornitore`;
    const body = `Buongiorno,\n\nRichiediamo il riordino dei seguenti articoli attualmente sotto la scorta minima:\n\n${lines}\n\nRestiamo in attesa di conferma e tempi di consegna.\n\nCordiali saluti,\nMekanika`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const invioAutomatico = async () => {
    if (withSupplier.length === 0) return toast.error("Nessun articolo con fornitore associato.");
    if (!window.confirm(`Inviare gli ordini di riordino ai fornitori (${withSupplier.length} articoli)?`)) return;
    setSending(true);
    try {
      const { data: res } = await api.post("/orders/send-auto");
      if (res.sent_count > 0) {
        const names = res.sent.map((s) => `${s.supplier_name} (${s.items_count})`).join(", ");
        toast.success(`Ordini inviati a ${res.sent_count} fornitori: ${names}`);
      } else {
        toast.info("Nessun ordine inviato.");
      }
      if (res.missing_count > 0) toast.warning(`${res.missing_count} articoli senza fornitore non inviati.`);
      load();
    } catch (e) {
      toast.error("Errore durante l'invio degli ordini.");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Report Riordini</h1>
        <p className="text-slate-500 text-sm mt-1">Articoli di magazzino sotto scorta e componenti elettrici collegati da ordinare.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        {isAdmin && (
          <button onClick={invioAutomatico} disabled={sending || withSupplier.length === 0} data-testid="reorder-invio-auto" className="h-10 px-4 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-950 font-semibold text-sm flex items-center gap-2 transition-colors">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invio Automatico Ordine ({withSupplier.length})
          </button>
        )}
        <button onClick={() => exportData("xlsx")} disabled={exporting || data.count === 0} data-testid="reorder-export-excel" className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-semibold text-sm flex items-center gap-2 transition-colors">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Esporta Excel
        </button>
        <button onClick={() => exportData("pdf")} disabled={exporting || data.count === 0} data-testid="reorder-export-pdf" className="h-10 px-4 rounded-lg bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-semibold text-sm flex items-center gap-2 transition-colors">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Esporta PDF
        </button>
      </div>

      {withoutSupplier.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 flex flex-wrap items-center gap-3" data-testid="reorder-missing-supplier">
          <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0" />
          <div className="text-sm text-amber-200 flex-1 min-w-[200px]">
            <strong>{withoutSupplier.length} articoli</strong> sotto scorta non hanno un fornitore associato e non verranno inviati automaticamente. Assegna un fornitore in Magazzino, oppure usa Ordine Rapido.
          </div>
          <button onClick={ordineRapido} data-testid="reorder-ordine-rapido" className="h-9 px-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm flex items-center gap-2 transition-colors">
            <Mail className="h-4 w-4" /> Ordine Rapido
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4" data-testid="reorder-stat-items">
          <div className="text-xs font-mono-data uppercase tracking-wider text-slate-500">Articoli sotto scorta</div>
          <div className="mt-1 text-3xl font-heading font-extrabold text-rose-400">{data.count}</div>
        </div>
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4" data-testid="reorder-stat-components">
          <div className="text-xs font-mono-data uppercase tracking-wider text-slate-500">Componenti elettrici collegati</div>
          <div className="mt-1 text-3xl font-heading font-extrabold text-amber-400">{data.components_count}</div>
        </div>
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4" data-testid="reorder-stat-suppliers">
          <div className="text-xs font-mono-data uppercase tracking-wider text-slate-500">Con fornitore</div>
          <div className="mt-1 text-3xl font-heading font-extrabold text-emerald-400">{withSupplier.length}</div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : data.items.length === 0 ? (
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-10 text-center" data-testid="reorder-empty">
          <PackageX className="h-10 w-10 mx-auto text-emerald-400 mb-3" />
          <p className="text-slate-300 font-medium">Nessun articolo sotto scorta</p>
          <p className="text-slate-500 text-sm mt-1">Tutte le giacenze sono sopra la soglia di riordino.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {data.items.map((it) => (
            <div key={it.id} data-testid={`reorder-item-${it.id}`} className="rounded-lg border border-rose-500/30 bg-slate-900/60 overflow-hidden">
              <div className="flex flex-wrap items-center gap-3 px-5 py-4 bg-rose-500/5">
                <div className="h-10 w-10 rounded-lg bg-rose-500/10 border border-rose-500/30 flex items-center justify-center shrink-0">
                  <ShoppingCart className="h-5 w-5 text-rose-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-bold truncate">{it.name}</div>
                  <div className="text-xs text-slate-500 font-mono-data flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span className="text-amber-400">{it.codice_prodotto}</span>
                    {it.codice_azienda && <span>az. {it.codice_azienda}</span>}
                    {it.ubicazione && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{it.ubicazione}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm font-mono-data">
                  <div className="text-center">
                    <div className="text-[10px] uppercase text-slate-500">Giacenza</div>
                    <div className="text-rose-400 font-bold text-lg">{it.quantity}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] uppercase text-slate-500">Soglia</div>
                    <div className="text-slate-300 text-lg">{it.reorder_threshold}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] uppercase text-slate-500">Da ordinare</div>
                    <div className="text-amber-400 font-bold text-lg">{it.to_order}</div>
                  </div>
                </div>
              </div>
              <div className="px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-2">
                <div data-testid={`reorder-supplier-${it.id}`} className="text-xs flex items-center gap-1.5">
                  <Truck className="h-3.5 w-3.5 text-slate-500" />
                  {it.supplier_name
                    ? <span className="text-emerald-400">{it.supplier_name} <span className="text-slate-500">· {it.supplier_email}</span></span>
                    : <span className="text-amber-400">Nessun fornitore</span>}
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-1"><Zap className="h-3 w-3 text-amber-400" /> Componenti elettrici collegati ({it.components.length})</div>
                  {it.components.length === 0 ? (
                    <div className="text-[12px] text-slate-600 flex items-center gap-1"><Warehouse className="h-3.5 w-3.5" /> Nessun componente elettrico collegato</div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {it.components.map((c) => (
                        <div key={c.id} data-testid={`reorder-comp-${c.id}`} className="rounded-lg border border-slate-700 bg-slate-950/50 px-3 py-1.5">
                          <div className="text-xs font-medium">{c.name}</div>
                          <div className="text-[10px] text-slate-500 font-mono-data">{c.brand} · <span className="text-amber-400">{c.codice_prodotto}</span></div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
